/**
 * Execution Trace Viewer — timeline + inspector + optional raw console.
 */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Activity, History, LayoutList, Terminal, Trash2, Radio } from 'lucide-react';
import Console from '../Console';
import TraceTimeline from './TraceTimeline';
import TraceInspector from './TraceInspector';
import { buildTimelineNodes, traceEventsOnly } from '@/utils/traceModel';

export default function ExecutionTraceViewer({
  status,
  logs,
  /** Task id for S3 merge: from Data → Output → logs/&lt;id&gt;/ or last execute */
  mergeTaskId,
  /** True when mergeTaskId comes from browsing output/logs, not only last run */
  mergeFromOutputFolder = false,
  onMergeS3,
  s3Loading,
  onClearLogs
}) {
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [panelMode, setPanelMode] = useState('trace'); // trace | raw
  const [followTail, setFollowTail] = useState(true);

  const nodes = useMemo(() => buildTimelineNodes(logs || []), [logs]);
  const traceCount = useMemo(() => traceEventsOnly(logs || []).length, [logs]);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  useEffect(() => {
    if (!nodes.length) {
      setSelectedNodeId(null);
      return;
    }
    if (followTail) {
      setSelectedNodeId(nodes[nodes.length - 1].id);
      return;
    }
    setSelectedNodeId((prev) => {
      if (prev && nodes.some((n) => n.id === prev)) return prev;
      return nodes[nodes.length - 1].id;
    });
  }, [nodes, followTail]);

  const onSelectNode = useCallback(
    (id) => {
      setFollowTail(false);
      setSelectedNodeId(id);
    },
    []
  );

  const statusConfig = {
    idle: { color: 'bg-gray-400', label: 'Idle' },
    uploading: { color: 'bg-blue-500 animate-pulse', label: 'Uploading' },
    processing: { color: 'bg-amber-500 animate-pulse', label: 'Running' },
    completed: { color: 'bg-emerald-500', label: 'Completed' },
    error: { color: 'bg-red-500', label: 'Error' }
  };
  const sc = statusConfig[status] || statusConfig.idle;

  return (
    <div className="h-full flex flex-col bg-slate-50 text-gray-900">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-3 py-2.5 bg-white border-b border-gray-200 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 pr-2 border-r border-gray-200">
          <span className={`w-2.5 h-2.5 rounded-full ${sc.color}`} title={sc.label} />
          <span className="text-xs font-medium text-gray-700">{sc.label}</span>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg bg-gray-100 p-0.5">
          <button
            type="button"
            onClick={() => setPanelMode('trace')}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              panelMode === 'trace' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <LayoutList className="w-3.5 h-3.5" />
            Trace
          </button>
          <button
            type="button"
            onClick={() => setPanelMode('raw')}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              panelMode === 'raw' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Terminal className="w-3.5 h-3.5" />
            Raw log
          </button>
        </div>

        {panelMode === 'trace' && (
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={followTail}
              onChange={(e) => setFollowTail(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Radio className="w-3.5 h-3.5 text-gray-400" />
            Follow latest step
          </label>
        )}

        <div className="min-w-0 flex-1 flex items-center justify-end gap-2">
          {mergeTaskId && (
            <span
              className="text-[11px] text-gray-500 font-mono truncate max-w-[200px] text-right"
              title={
                mergeFromOutputFolder
                  ? `S3 merge target: ${mergeTaskId} (from Data → Output folder)`
                  : `S3 merge target: ${mergeTaskId}`
              }
            >
              <span className="text-gray-400">{mergeFromOutputFolder ? 'Folder' : 'Task'}</span>{' '}
              <span className="text-gray-700">{mergeTaskId.slice(0, 8)}…</span>
            </span>
          )}
          <span className="text-[11px] text-gray-400 hidden sm:inline">
            {traceCount} trace event{traceCount === 1 ? '' : 's'}
          </span>
          {mergeTaskId && onMergeS3 && (
            <button
              type="button"
              onClick={onMergeS3}
              disabled={s3Loading}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              title="Load JSON logs from S3 for this task into the trace"
            >
              <History className="w-3.5 h-3.5" />
              {s3Loading ? 'Loading…' : 'Merge S3 logs'}
            </button>
          )}
          {onClearLogs && (
            <button
              type="button"
              onClick={onClearLogs}
              className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </button>
          )}
        </div>
      </div>

      {panelMode === 'raw' ? (
        <div className="flex-1 min-h-0 overflow-hidden p-2">
          <div className="h-full rounded-lg overflow-hidden border border-gray-200">
            <Console status={status} logs={logs} headerActions={null} />
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex overflow-hidden">
          <aside className="w-[min(100%,320px)] shrink-0 border-r border-gray-200 bg-white flex flex-col min-h-0">
            <div className="flex-shrink-0 px-3 py-2 border-b border-gray-100 flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wide">
              <Activity className="w-4 h-4 text-indigo-500" />
              Timeline
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <TraceTimeline nodes={nodes} selectedId={selectedNodeId} onSelect={onSelectNode} />
            </div>
          </aside>
          <main className="flex-1 min-w-0 min-h-0 bg-slate-50/80">
            <TraceInspector node={selectedNode} />
          </main>
        </div>
      )}
    </div>
  );
}
